import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  Save,
  Eye,
  Loader2,
  Image as ImageIcon,
  Type,
  Quote,
  List,
  Calendar,
  DollarSign,
  MessageSquare,
  User,
  HelpCircle,
  Sparkles,
  Plus,
  Trash2,
  GripVertical,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import api from '../../lib/api';
import FileUpload from '../../components/FileUpload';

const AdminLandingEditor = () => {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('hero');
  const [expandedSections, setExpandedSections] = useState({});

  const tabs = [
    { id: 'hero', label: 'Hero', icon: ImageIcon },
    { id: 'introduction', label: 'Introducción', icon: Type },
    { id: 'self_support', label: 'El Sostén', icon: Quote },
    { id: 'structure', label: 'Estructura', icon: List },
    { id: 'learning', label: 'Aprendizajes', icon: Sparkles },
    { id: 'dates', label: 'Fechas', icon: Calendar },
    { id: 'pricing', label: 'Precios', icon: DollarSign },
    { id: 'testimonials', label: 'Testimonios', icon: MessageSquare },
    { id: 'instructor', label: 'Instructor', icon: User },
    { id: 'faqs', label: 'FAQs', icon: HelpCircle },
    { id: 'final_cta', label: 'CTA Final', icon: Sparkles }
  ];

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await api.get('/special-courses/facilitadoras-sosten-energetico/landing-content');
      setContent(response.data);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al cargar el contenido');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/admin/special-courses/facilitadoras-sosten-energetico/landing-content', content);
      toast.success('Contenido guardado correctamente');
    } catch (error) {
      toast.error('Error al guardar los cambios');
    } finally {
      setSaving(false);
    }
  };

  const updateField = (section, field, value) => {
    setContent(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const updateNestedField = (section, parent, field, value) => {
    setContent(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [parent]: {
          ...prev[section][parent],
          [field]: value
        }
      }
    }));
  };

  const updateArrayItem = (section, field, index, value) => {
    setContent(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: prev[section][field].map((item, i) => i === index ? value : item)
      }
    }));
  };

  const addArrayItem = (section, field, defaultValue) => {
    setContent(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: [...prev[section][field], defaultValue]
      }
    }));
  };

  const removeArrayItem = (section, field, index) => {
    setContent(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: prev[section][field].filter((_, i) => i !== index)
      }
    }));
  };

  const toggleExpanded = (key) => {
    setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  if (loading) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[hsl(var(--accent))]" />
      </div>
    );
  }

  const renderHeroEditor = () => (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-[hsl(var(--text-secondary))] mb-2">
          Imagen de fondo
        </label>
        <FileUpload
          value={content.hero.background_image}
          onChange={(url) => updateField('hero', 'background_image', url)}
          accept="image/*"
          previewClassName="h-48"
        />
      </div>
      
      <div>
        <label className="block text-sm font-medium text-[hsl(var(--text-secondary))] mb-2">
          Badge (etiqueta superior)
        </label>
        <Input
          value={content.hero.badge_text}
          onChange={(e) => updateField('hero', 'badge_text', e.target.value)}
        />
      </div>
      
      <div>
        <label className="block text-sm font-medium text-[hsl(var(--text-secondary))] mb-2">
          Título principal
        </label>
        <Input
          value={content.hero.title}
          onChange={(e) => updateField('hero', 'title', e.target.value)}
        />
      </div>
      
      <div>
        <label className="block text-sm font-medium text-[hsl(var(--text-secondary))] mb-2">
          Rango de fechas
        </label>
        <Input
          value={content.hero.date_range}
          onChange={(e) => updateField('hero', 'date_range', e.target.value)}
        />
      </div>
      
      <div>
        <label className="block text-sm font-medium text-[hsl(var(--text-secondary))] mb-2">
          Descripción
        </label>
        <Textarea
          value={content.hero.description}
          onChange={(e) => updateField('hero', 'description', e.target.value)}
          rows={3}
        />
      </div>
      
      <div>
        <label className="block text-sm font-medium text-[hsl(var(--text-secondary))] mb-2">
          Cita destacada
        </label>
        <Textarea
          value={content.hero.quote}
          onChange={(e) => updateField('hero', 'quote', e.target.value)}
          rows={3}
        />
      </div>
      
      <div>
        <label className="block text-sm font-medium text-[hsl(var(--text-secondary))] mb-2">
          Texto del botón CTA
        </label>
        <Input
          value={content.hero.cta_text}
          onChange={(e) => updateField('hero', 'cta_text', e.target.value)}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-[hsl(var(--text-secondary))] mb-2">
          Características (iconos)
        </label>
        <div className="space-y-2">
          {content.hero.features.map((feature, idx) => (
            <div key={idx} className="flex items-center gap-2">
              <select
                value={feature.icon}
                onChange={(e) => updateArrayItem('hero', 'features', idx, { ...feature, icon: e.target.value })}
                className="p-2 border rounded-lg text-sm"
              >
                <option value="monitor">Monitor</option>
                <option value="map_pin">Ubicación</option>
                <option value="users">Usuarios</option>
                <option value="clock">Reloj</option>
                <option value="calendar">Calendario</option>
              </select>
              <Input
                value={feature.text}
                onChange={(e) => updateArrayItem('hero', 'features', idx, { ...feature, text: e.target.value })}
                className="flex-1"
              />
              <Button
                variant="ghost"
                size="sm"
                onClick={() => removeArrayItem('hero', 'features', idx)}
              >
                <Trash2 className="w-4 h-4 text-red-500" />
              </Button>
            </div>
          ))}
          <Button
            variant="outline"
            size="sm"
            onClick={() => addArrayItem('hero', 'features', { icon: 'monitor', text: '' })}
          >
            <Plus className="w-4 h-4 mr-1" /> Añadir característica
          </Button>
        </div>
      </div>
    </div>
  );

  const renderIntroductionEditor = () => (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-[hsl(var(--text-secondary))] mb-2">
          Título de sección
        </label>
        <Input
          value={content.introduction.title}
          onChange={(e) => updateField('introduction', 'title', e.target.value)}
        />
      </div>
      
      <div>
        <label className="block text-sm font-medium text-[hsl(var(--text-secondary))] mb-2">
          Texto destacado (dorado)
        </label>
        <Textarea
          value={content.introduction.highlight_text}
          onChange={(e) => updateField('introduction', 'highlight_text', e.target.value)}
          rows={3}
        />
      </div>
      
      <div>
        <label className="block text-sm font-medium text-[hsl(var(--text-secondary))] mb-2">
          Párrafos
        </label>
        <div className="space-y-3">
          {content.introduction.paragraphs.map((para, idx) => (
            <div key={idx} className="flex gap-2">
              <Textarea
                value={para}
                onChange={(e) => updateArrayItem('introduction', 'paragraphs', idx, e.target.value)}
                rows={2}
                className="flex-1"
              />
              <Button
                variant="ghost"
                size="sm"
                onClick={() => removeArrayItem('introduction', 'paragraphs', idx)}
              >
                <Trash2 className="w-4 h-4 text-red-500" />
              </Button>
            </div>
          ))}
          <Button
            variant="outline"
            size="sm"
            onClick={() => addArrayItem('introduction', 'paragraphs', '')}
          >
            <Plus className="w-4 h-4 mr-1" /> Añadir párrafo
          </Button>
        </div>
      </div>
    </div>
  );

  const renderSelfSupportEditor = () => (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-[hsl(var(--text-secondary))] mb-2">
          Título
        </label>
        <Input
          value={content.self_support.title}
          onChange={(e) => updateField('self_support', 'title', e.target.value)}
        />
      </div>
      
      <div>
        <label className="block text-sm font-medium text-[hsl(var(--text-secondary))] mb-2">
          Texto introductorio
        </label>
        <Textarea
          value={content.self_support.intro_text}
          onChange={(e) => updateField('self_support', 'intro_text', e.target.value)}
          rows={3}
        />
      </div>
      
      <div>
        <label className="block text-sm font-medium text-[hsl(var(--text-secondary))] mb-2">
          Beneficios (lista)
        </label>
        <div className="space-y-2">
          {content.self_support.benefits.map((benefit, idx) => (
            <div key={idx} className="flex gap-2">
              <Input
                value={benefit}
                onChange={(e) => updateArrayItem('self_support', 'benefits', idx, e.target.value)}
                className="flex-1"
              />
              <Button
                variant="ghost"
                size="sm"
                onClick={() => removeArrayItem('self_support', 'benefits', idx)}
              >
                <Trash2 className="w-4 h-4 text-red-500" />
              </Button>
            </div>
          ))}
          <Button
            variant="outline"
            size="sm"
            onClick={() => addArrayItem('self_support', 'benefits', '')}
          >
            <Plus className="w-4 h-4 mr-1" /> Añadir beneficio
          </Button>
        </div>
      </div>
      
      <div>
        <label className="block text-sm font-medium text-[hsl(var(--text-secondary))] mb-2">
          Texto de cierre
        </label>
        <Textarea
          value={content.self_support.closing_text}
          onChange={(e) => updateField('self_support', 'closing_text', e.target.value)}
          rows={3}
        />
      </div>
    </div>
  );

  const renderStructureEditor = () => (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-[hsl(var(--text-secondary))] mb-2">
          Título de sección
        </label>
        <Input
          value={content.structure.title}
          onChange={(e) => updateField('structure', 'title', e.target.value)}
        />
      </div>
      
      <div>
        <label className="block text-sm font-medium text-[hsl(var(--text-secondary))] mb-2">
          Subtítulo
        </label>
        <Input
          value={content.structure.subtitle}
          onChange={(e) => updateField('structure', 'subtitle', e.target.value)}
        />
      </div>
      
      {/* Online Section */}
      <div className="p-4 bg-[hsl(var(--accent))]/5 rounded-xl">
        <h3 className="font-medium text-[hsl(var(--text-primary))] mb-4">📡 Parte Online</h3>
        <div className="space-y-3">
          <Input
            value={content.structure.online.title}
            onChange={(e) => updateNestedField('structure', 'online', 'title', e.target.value)}
            placeholder="Título"
          />
          <Input
            value={content.structure.online.badge}
            onChange={(e) => updateNestedField('structure', 'online', 'badge', e.target.value)}
            placeholder="Badge"
          />
          <Textarea
            value={content.structure.online.description}
            onChange={(e) => updateNestedField('structure', 'online', 'description', e.target.value)}
            placeholder="Descripción"
            rows={2}
          />
          <div>
            <label className="text-xs text-[hsl(var(--text-tertiary))]">Características</label>
            {content.structure.online.features.map((feature, idx) => (
              <div key={idx} className="flex gap-2 mt-1">
                <Input
                  value={feature}
                  onChange={(e) => {
                    const newFeatures = [...content.structure.online.features];
                    newFeatures[idx] = e.target.value;
                    updateNestedField('structure', 'online', 'features', newFeatures);
                  }}
                  className="flex-1 text-sm"
                />
                <Button variant="ghost" size="sm" onClick={() => {
                  const newFeatures = content.structure.online.features.filter((_, i) => i !== idx);
                  updateNestedField('structure', 'online', 'features', newFeatures);
                }}>
                  <Trash2 className="w-3 h-3 text-red-500" />
                </Button>
              </div>
            ))}
            <Button variant="outline" size="sm" className="mt-2" onClick={() => {
              const newFeatures = [...content.structure.online.features, ''];
              updateNestedField('structure', 'online', 'features', newFeatures);
            }}>
              <Plus className="w-3 h-3 mr-1" /> Añadir
            </Button>
          </div>
          <Textarea
            value={content.structure.online.note}
            onChange={(e) => updateNestedField('structure', 'online', 'note', e.target.value)}
            placeholder="Nota destacada"
            rows={2}
          />
        </div>
      </div>
      
      {/* Presencial Section */}
      <div className="p-4 bg-[hsl(var(--sage))]/10 rounded-xl">
        <h3 className="font-medium text-[hsl(var(--text-primary))] mb-4">🤝 Parte Presencial</h3>
        <div className="space-y-3">
          <Input
            value={content.structure.presencial.title}
            onChange={(e) => updateNestedField('structure', 'presencial', 'title', e.target.value)}
            placeholder="Título"
          />
          <Input
            value={content.structure.presencial.badge}
            onChange={(e) => updateNestedField('structure', 'presencial', 'badge', e.target.value)}
            placeholder="Badge"
          />
          <Textarea
            value={content.structure.presencial.description}
            onChange={(e) => updateNestedField('structure', 'presencial', 'description', e.target.value)}
            placeholder="Descripción"
            rows={2}
          />
          <div>
            <label className="text-xs text-[hsl(var(--text-tertiary))]">Características</label>
            {content.structure.presencial.features.map((feature, idx) => (
              <div key={idx} className="flex gap-2 mt-1">
                <Input
                  value={feature}
                  onChange={(e) => {
                    const newFeatures = [...content.structure.presencial.features];
                    newFeatures[idx] = e.target.value;
                    updateNestedField('structure', 'presencial', 'features', newFeatures);
                  }}
                  className="flex-1 text-sm"
                />
                <Button variant="ghost" size="sm" onClick={() => {
                  const newFeatures = content.structure.presencial.features.filter((_, i) => i !== idx);
                  updateNestedField('structure', 'presencial', 'features', newFeatures);
                }}>
                  <Trash2 className="w-3 h-3 text-red-500" />
                </Button>
              </div>
            ))}
            <Button variant="outline" size="sm" className="mt-2" onClick={() => {
              const newFeatures = [...content.structure.presencial.features, ''];
              updateNestedField('structure', 'presencial', 'features', newFeatures);
            }}>
              <Plus className="w-3 h-3 mr-1" /> Añadir
            </Button>
          </div>
          <Textarea
            value={content.structure.presencial.note}
            onChange={(e) => updateNestedField('structure', 'presencial', 'note', e.target.value)}
            placeholder="Nota destacada"
            rows={2}
          />
        </div>
      </div>
    </div>
  );

  const renderLearningEditor = () => (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-[hsl(var(--text-secondary))] mb-2">
          Título
        </label>
        <Input
          value={content.learning.title}
          onChange={(e) => updateField('learning', 'title', e.target.value)}
        />
      </div>
      
      <div>
        <label className="block text-sm font-medium text-[hsl(var(--text-secondary))] mb-2">
          Aprendizajes
        </label>
        <div className="space-y-2">
          {content.learning.items.map((item, idx) => (
            <div key={idx} className="flex gap-2">
              <Input
                value={item}
                onChange={(e) => updateArrayItem('learning', 'items', idx, e.target.value)}
                className="flex-1"
              />
              <Button
                variant="ghost"
                size="sm"
                onClick={() => removeArrayItem('learning', 'items', idx)}
              >
                <Trash2 className="w-4 h-4 text-red-500" />
              </Button>
            </div>
          ))}
          <Button
            variant="outline"
            size="sm"
            onClick={() => addArrayItem('learning', 'items', '')}
          >
            <Plus className="w-4 h-4 mr-1" /> Añadir aprendizaje
          </Button>
        </div>
      </div>
    </div>
  );

  const renderDatesEditor = () => (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-[hsl(var(--text-secondary))] mb-2">
          Título de sección
        </label>
        <Input
          value={content.dates.title}
          onChange={(e) => updateField('dates', 'title', e.target.value)}
        />
      </div>
      
      {/* Online Dates */}
      <div className="p-4 bg-[hsl(var(--accent))]/5 rounded-xl">
        <h3 className="font-medium text-[hsl(var(--text-primary))] mb-4">📡 Fechas Online</h3>
        <div className="space-y-3">
          <Input
            value={content.dates.online.title}
            onChange={(e) => updateNestedField('dates', 'online', 'title', e.target.value)}
            placeholder="Título"
          />
          <Input
            value={content.dates.online.schedule}
            onChange={(e) => updateNestedField('dates', 'online', 'schedule', e.target.value)}
            placeholder="Horario"
          />
          <div>
            <label className="text-xs text-[hsl(var(--text-tertiary))]">Meses y días</label>
            {content.dates.online.months.map((month, idx) => (
              <div key={idx} className="flex gap-2 mt-1">
                <Input
                  value={month.name}
                  onChange={(e) => {
                    const newMonths = [...content.dates.online.months];
                    newMonths[idx] = { ...month, name: e.target.value };
                    updateNestedField('dates', 'online', 'months', newMonths);
                  }}
                  placeholder="Mes"
                  className="w-32"
                />
                <Input
                  value={month.days}
                  onChange={(e) => {
                    const newMonths = [...content.dates.online.months];
                    newMonths[idx] = { ...month, days: e.target.value };
                    updateNestedField('dates', 'online', 'months', newMonths);
                  }}
                  placeholder="Días"
                  className="flex-1"
                />
                <Button variant="ghost" size="sm" onClick={() => {
                  const newMonths = content.dates.online.months.filter((_, i) => i !== idx);
                  updateNestedField('dates', 'online', 'months', newMonths);
                }}>
                  <Trash2 className="w-3 h-3 text-red-500" />
                </Button>
              </div>
            ))}
            <Button variant="outline" size="sm" className="mt-2" onClick={() => {
              const newMonths = [...content.dates.online.months, { name: '', days: '' }];
              updateNestedField('dates', 'online', 'months', newMonths);
            }}>
              <Plus className="w-3 h-3 mr-1" /> Añadir mes
            </Button>
          </div>
          <Input
            value={content.dates.online.note}
            onChange={(e) => updateNestedField('dates', 'online', 'note', e.target.value)}
            placeholder="Nota"
          />
        </div>
      </div>
      
      {/* Presencial Dates */}
      <div className="p-4 bg-[hsl(var(--sage))]/10 rounded-xl">
        <h3 className="font-medium text-[hsl(var(--text-primary))] mb-4">🤝 Fechas Presenciales</h3>
        <div className="space-y-3">
          <Input
            value={content.dates.presencial.title}
            onChange={(e) => updateNestedField('dates', 'presencial', 'title', e.target.value)}
            placeholder="Título"
          />
          <Input
            value={content.dates.presencial.schedule}
            onChange={(e) => updateNestedField('dates', 'presencial', 'schedule', e.target.value)}
            placeholder="Número de encuentros"
          />
          <div>
            <label className="text-xs text-[hsl(var(--text-tertiary))]">Eventos</label>
            {content.dates.presencial.events.map((event, idx) => (
              <div key={idx} className="flex gap-2 mt-1">
                <Input
                  value={event.label}
                  onChange={(e) => {
                    const newEvents = [...content.dates.presencial.events];
                    newEvents[idx] = { ...event, label: e.target.value };
                    updateNestedField('dates', 'presencial', 'events', newEvents);
                  }}
                  placeholder="Etiqueta"
                  className="w-32"
                />
                <Input
                  value={event.dates}
                  onChange={(e) => {
                    const newEvents = [...content.dates.presencial.events];
                    newEvents[idx] = { ...event, dates: e.target.value };
                    updateNestedField('dates', 'presencial', 'events', newEvents);
                  }}
                  placeholder="Fechas"
                  className="flex-1"
                />
                <Button variant="ghost" size="sm" onClick={() => {
                  const newEvents = content.dates.presencial.events.filter((_, i) => i !== idx);
                  updateNestedField('dates', 'presencial', 'events', newEvents);
                }}>
                  <Trash2 className="w-3 h-3 text-red-500" />
                </Button>
              </div>
            ))}
            <Button variant="outline" size="sm" className="mt-2" onClick={() => {
              const newEvents = [...content.dates.presencial.events, { label: '', dates: '' }];
              updateNestedField('dates', 'presencial', 'events', newEvents);
            }}>
              <Plus className="w-3 h-3 mr-1" /> Añadir evento
            </Button>
          </div>
          <Input
            value={content.dates.presencial.hours}
            onChange={(e) => updateNestedField('dates', 'presencial', 'hours', e.target.value)}
            placeholder="Horarios"
          />
          <Input
            value={content.dates.presencial.note}
            onChange={(e) => updateNestedField('dates', 'presencial', 'note', e.target.value)}
            placeholder="Nota"
          />
        </div>
      </div>
    </div>
  );

  const renderPricingEditor = () => (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-[hsl(var(--text-secondary))] mb-2">
          Título
        </label>
        <Input
          value={content.pricing.title}
          onChange={(e) => updateField('pricing', 'title', e.target.value)}
        />
      </div>
      
      <div>
        <label className="block text-sm font-medium text-[hsl(var(--text-secondary))] mb-2">
          Subtítulo
        </label>
        <Textarea
          value={content.pricing.subtitle}
          onChange={(e) => updateField('pricing', 'subtitle', e.target.value)}
          rows={2}
        />
      </div>
      
      {/* Online Pricing */}
      <div className="p-4 bg-[hsl(var(--accent))]/5 rounded-xl">
        <h3 className="font-medium text-[hsl(var(--text-primary))] mb-4">💻 Precio Online</h3>
        <div className="space-y-3">
          <Input
            value={content.pricing.online.title}
            onChange={(e) => updateNestedField('pricing', 'online', 'title', e.target.value)}
            placeholder="Título"
          />
          <Input
            value={content.pricing.online.subtitle}
            onChange={(e) => updateNestedField('pricing', 'online', 'subtitle', e.target.value)}
            placeholder="Subtítulo"
          />
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-[hsl(var(--text-tertiary))]">Pronto pago</label>
              <Input
                value={content.pricing.online.pronto_pago.price}
                onChange={(e) => {
                  updateNestedField('pricing', 'online', 'pronto_pago', {
                    ...content.pricing.online.pronto_pago,
                    price: e.target.value
                  });
                }}
                placeholder="Precio"
              />
              <Input
                value={content.pricing.online.pronto_pago.savings}
                onChange={(e) => {
                  updateNestedField('pricing', 'online', 'pronto_pago', {
                    ...content.pricing.online.pronto_pago,
                    savings: e.target.value
                  });
                }}
                placeholder="Ahorro"
                className="mt-1"
              />
            </div>
            <div>
              <label className="text-xs text-[hsl(var(--text-tertiary))]">Cuotas</label>
              <Input
                value={content.pricing.online.cuotas.price}
                onChange={(e) => {
                  updateNestedField('pricing', 'online', 'cuotas', {
                    ...content.pricing.online.cuotas,
                    price: e.target.value
                  });
                }}
                placeholder="Precio por cuota"
              />
              <Input
                value={content.pricing.online.cuotas.total}
                onChange={(e) => {
                  updateNestedField('pricing', 'online', 'cuotas', {
                    ...content.pricing.online.cuotas,
                    total: e.target.value
                  });
                }}
                placeholder="Total"
                className="mt-1"
              />
            </div>
          </div>
        </div>
      </div>
      
      {/* Presencial Pricing */}
      <div className="p-4 bg-[hsl(var(--sage))]/10 rounded-xl">
        <h3 className="font-medium text-[hsl(var(--text-primary))] mb-4">🤲 Precio Presencial</h3>
        <div className="space-y-3">
          <Input
            value={content.pricing.presencial.title}
            onChange={(e) => updateNestedField('pricing', 'presencial', 'title', e.target.value)}
            placeholder="Título"
          />
          <Input
            value={content.pricing.presencial.subtitle}
            onChange={(e) => updateNestedField('pricing', 'presencial', 'subtitle', e.target.value)}
            placeholder="Subtítulo"
          />
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-[hsl(var(--text-tertiary))]">Pronto pago</label>
              <Input
                value={content.pricing.presencial.pronto_pago.price}
                onChange={(e) => {
                  updateNestedField('pricing', 'presencial', 'pronto_pago', {
                    ...content.pricing.presencial.pronto_pago,
                    price: e.target.value
                  });
                }}
                placeholder="Precio"
              />
              <Input
                value={content.pricing.presencial.pronto_pago.savings}
                onChange={(e) => {
                  updateNestedField('pricing', 'presencial', 'pronto_pago', {
                    ...content.pricing.presencial.pronto_pago,
                    savings: e.target.value
                  });
                }}
                placeholder="Ahorro"
                className="mt-1"
              />
            </div>
            <div>
              <label className="text-xs text-[hsl(var(--text-tertiary))]">Cuotas</label>
              <Input
                value={content.pricing.presencial.cuotas.price}
                onChange={(e) => {
                  updateNestedField('pricing', 'presencial', 'cuotas', {
                    ...content.pricing.presencial.cuotas,
                    price: e.target.value
                  });
                }}
                placeholder="Precio por cuota"
              />
              <Input
                value={content.pricing.presencial.cuotas.total}
                onChange={(e) => {
                  updateNestedField('pricing', 'presencial', 'cuotas', {
                    ...content.pricing.presencial.cuotas,
                    total: e.target.value
                  });
                }}
                placeholder="Total"
                className="mt-1"
              />
            </div>
          </div>
        </div>
      </div>
      
      {/* Pack Pricing */}
      <div className="p-4 bg-gradient-to-r from-[hsl(var(--accent))]/10 to-[hsl(var(--sage))]/10 rounded-xl border-2 border-[hsl(var(--accent))]/20">
        <h3 className="font-medium text-[hsl(var(--text-primary))] mb-4">🌟 Pack Completo</h3>
        <div className="space-y-3">
          <Input
            value={content.pricing.pack.title}
            onChange={(e) => updateNestedField('pricing', 'pack', 'title', e.target.value)}
            placeholder="Título"
          />
          <Input
            value={content.pricing.pack.subtitle}
            onChange={(e) => updateNestedField('pricing', 'pack', 'subtitle', e.target.value)}
            placeholder="Subtítulo"
          />
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-[hsl(var(--text-tertiary))]">Pronto pago</label>
              <Input
                value={content.pricing.pack.pronto_pago.price}
                onChange={(e) => {
                  updateNestedField('pricing', 'pack', 'pronto_pago', {
                    ...content.pricing.pack.pronto_pago,
                    price: e.target.value
                  });
                }}
                placeholder="Precio"
              />
              <Input
                value={content.pricing.pack.pronto_pago.savings}
                onChange={(e) => {
                  updateNestedField('pricing', 'pack', 'pronto_pago', {
                    ...content.pricing.pack.pronto_pago,
                    savings: e.target.value
                  });
                }}
                placeholder="Ahorro"
                className="mt-1"
              />
            </div>
            <div>
              <label className="text-xs text-[hsl(var(--text-tertiary))]">Cuotas</label>
              <Input
                value={content.pricing.pack.cuotas.price}
                onChange={(e) => {
                  updateNestedField('pricing', 'pack', 'cuotas', {
                    ...content.pricing.pack.cuotas,
                    price: e.target.value
                  });
                }}
                placeholder="Precio por cuota"
              />
              <Input
                value={content.pricing.pack.cuotas.total}
                onChange={(e) => {
                  updateNestedField('pricing', 'pack', 'cuotas', {
                    ...content.pricing.pack.cuotas,
                    total: e.target.value
                  });
                }}
                placeholder="Total"
                className="mt-1"
              />
            </div>
          </div>
        </div>
      </div>
      
      {/* Payment Info */}
      <div className="p-4 bg-gray-50 rounded-xl">
        <h3 className="font-medium text-[hsl(var(--text-primary))] mb-4">📋 Info de pago</h3>
        <Input
          value={content.pricing.payment_info.title}
          onChange={(e) => updateNestedField('pricing', 'payment_info', 'title', e.target.value)}
          placeholder="Título"
          className="mb-3"
        />
        <div className="space-y-2">
          {content.pricing.payment_info.items.map((item, idx) => (
            <div key={idx} className="flex gap-2">
              <Input
                value={item}
                onChange={(e) => {
                  const newItems = [...content.pricing.payment_info.items];
                  newItems[idx] = e.target.value;
                  updateNestedField('pricing', 'payment_info', 'items', newItems);
                }}
                className="flex-1"
              />
              <Button variant="ghost" size="sm" onClick={() => {
                const newItems = content.pricing.payment_info.items.filter((_, i) => i !== idx);
                updateNestedField('pricing', 'payment_info', 'items', newItems);
              }}>
                <Trash2 className="w-3 h-3 text-red-500" />
              </Button>
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={() => {
            const newItems = [...content.pricing.payment_info.items, ''];
            updateNestedField('pricing', 'payment_info', 'items', newItems);
          }}>
            <Plus className="w-3 h-3 mr-1" /> Añadir punto
          </Button>
        </div>
      </div>
    </div>
  );

  const renderTestimonialsEditor = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="p-4 bg-gray-50 rounded-xl">
        <h3 className="font-medium text-[hsl(var(--text-primary))] mb-4">Encabezado</h3>
        <div className="space-y-3">
          <Input
            value={content.testimonials.header.eyebrow}
            onChange={(e) => updateNestedField('testimonials', 'header', 'eyebrow', e.target.value)}
            placeholder="Texto pequeño superior"
          />
          <Input
            value={content.testimonials.header.title}
            onChange={(e) => updateNestedField('testimonials', 'header', 'title', e.target.value)}
            placeholder="Título"
          />
          <Textarea
            value={content.testimonials.header.subtitle}
            onChange={(e) => updateNestedField('testimonials', 'header', 'subtitle', e.target.value)}
            placeholder="Subtítulo"
            rows={2}
          />
        </div>
      </div>
      
      {/* Testimonials */}
      <div>
        <label className="block text-sm font-medium text-[hsl(var(--text-secondary))] mb-2">
          Testimonios
        </label>
        <div className="space-y-4">
          {content.testimonials.items.map((testimonial, idx) => (
            <div key={idx} className="p-4 border rounded-xl">
              <div className="flex justify-between items-start mb-3">
                <button
                  onClick={() => toggleExpanded(`testimonial-${idx}`)}
                  className="flex items-center gap-2 font-medium"
                >
                  {expandedSections[`testimonial-${idx}`] ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  {testimonial.name || `Testimonio ${idx + 1}`}
                </button>
                <Button variant="ghost" size="sm" onClick={() => removeArrayItem('testimonials', 'items', idx)}>
                  <Trash2 className="w-4 h-4 text-red-500" />
                </Button>
              </div>
              
              {expandedSections[`testimonial-${idx}`] && (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <Input
                      value={testimonial.name}
                      onChange={(e) => updateArrayItem('testimonials', 'items', idx, { ...testimonial, name: e.target.value })}
                      placeholder="Nombre"
                    />
                    <Input
                      value={testimonial.highlight}
                      onChange={(e) => updateArrayItem('testimonials', 'items', idx, { ...testimonial, highlight: e.target.value })}
                      placeholder="Frase destacada"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="text-xs text-[hsl(var(--text-tertiary))]">Color del avatar:</label>
                    <input
                      type="color"
                      value={testimonial.color}
                      onChange={(e) => updateArrayItem('testimonials', 'items', idx, { ...testimonial, color: e.target.value })}
                      className="w-10 h-8 rounded cursor-pointer"
                    />
                  </div>
                  <Textarea
                    value={testimonial.text}
                    onChange={(e) => updateArrayItem('testimonials', 'items', idx, { ...testimonial, text: e.target.value })}
                    placeholder="Texto del testimonio"
                    rows={4}
                  />
                </div>
              )}
            </div>
          ))}
          <Button
            variant="outline"
            onClick={() => addArrayItem('testimonials', 'items', { name: '', highlight: '', color: '#B8C4A8', text: '' })}
          >
            <Plus className="w-4 h-4 mr-1" /> Añadir testimonio
          </Button>
        </div>
      </div>
    </div>
  );

  const renderInstructorEditor = () => (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-[hsl(var(--text-secondary))] mb-2">
          Foto del instructor
        </label>
        <FileUpload
          value={content.instructor.image}
          onChange={(url) => updateField('instructor', 'image', url)}
          accept="image/*"
          previewClassName="h-48 w-48"
        />
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-[hsl(var(--text-secondary))] mb-2">
            Nombre
          </label>
          <Input
            value={content.instructor.name}
            onChange={(e) => updateField('instructor', 'name', e.target.value)}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-[hsl(var(--text-secondary))] mb-2">
            Rol
          </label>
          <Input
            value={content.instructor.role}
            onChange={(e) => updateField('instructor', 'role', e.target.value)}
          />
        </div>
      </div>
      
      <div>
        <label className="block text-sm font-medium text-[hsl(var(--text-secondary))] mb-2">
          Título de sección
        </label>
        <Input
          value={content.instructor.title}
          onChange={(e) => updateField('instructor', 'title', e.target.value)}
        />
      </div>
      
      <div>
        <label className="block text-sm font-medium text-[hsl(var(--text-secondary))] mb-2">
          Párrafos de bio
        </label>
        <div className="space-y-3">
          {content.instructor.paragraphs.map((para, idx) => (
            <div key={idx} className="flex gap-2">
              <Textarea
                value={para}
                onChange={(e) => updateArrayItem('instructor', 'paragraphs', idx, e.target.value)}
                rows={3}
                className="flex-1"
              />
              <Button
                variant="ghost"
                size="sm"
                onClick={() => removeArrayItem('instructor', 'paragraphs', idx)}
              >
                <Trash2 className="w-4 h-4 text-red-500" />
              </Button>
            </div>
          ))}
          <Button
            variant="outline"
            size="sm"
            onClick={() => addArrayItem('instructor', 'paragraphs', '')}
          >
            <Plus className="w-4 h-4 mr-1" /> Añadir párrafo
          </Button>
        </div>
      </div>
      
      <div>
        <label className="block text-sm font-medium text-[hsl(var(--text-secondary))] mb-2">
          Texto destacado (cita)
        </label>
        <Textarea
          value={content.instructor.highlight_text}
          onChange={(e) => updateField('instructor', 'highlight_text', e.target.value)}
          rows={3}
        />
      </div>
    </div>
  );

  const renderFaqsEditor = () => (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-[hsl(var(--text-secondary))] mb-2">
          Título de sección
        </label>
        <Input
          value={content.faqs.title}
          onChange={(e) => updateField('faqs', 'title', e.target.value)}
        />
      </div>
      
      <div>
        <label className="block text-sm font-medium text-[hsl(var(--text-secondary))] mb-2">
          Preguntas y respuestas
        </label>
        <div className="space-y-4">
          {content.faqs.items.map((faq, idx) => (
            <div key={idx} className="p-4 border rounded-xl">
              <div className="flex justify-between items-start mb-3">
                <button
                  onClick={() => toggleExpanded(`faq-${idx}`)}
                  className="flex items-center gap-2 font-medium text-left"
                >
                  {expandedSections[`faq-${idx}`] ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  <span className="line-clamp-1">{faq.question || `FAQ ${idx + 1}`}</span>
                </button>
                <Button variant="ghost" size="sm" onClick={() => removeArrayItem('faqs', 'items', idx)}>
                  <Trash2 className="w-4 h-4 text-red-500" />
                </Button>
              </div>
              
              {expandedSections[`faq-${idx}`] && (
                <div className="space-y-3">
                  <Input
                    value={faq.question}
                    onChange={(e) => updateArrayItem('faqs', 'items', idx, { ...faq, question: e.target.value })}
                    placeholder="Pregunta"
                  />
                  <Textarea
                    value={faq.answer}
                    onChange={(e) => updateArrayItem('faqs', 'items', idx, { ...faq, answer: e.target.value })}
                    placeholder="Respuesta"
                    rows={4}
                  />
                </div>
              )}
            </div>
          ))}
          <Button
            variant="outline"
            onClick={() => addArrayItem('faqs', 'items', { question: '', answer: '' })}
          >
            <Plus className="w-4 h-4 mr-1" /> Añadir pregunta
          </Button>
        </div>
      </div>
    </div>
  );

  const renderFinalCtaEditor = () => (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-[hsl(var(--text-secondary))] mb-2">
          Título
        </label>
        <Input
          value={content.final_cta.title}
          onChange={(e) => updateField('final_cta', 'title', e.target.value)}
        />
      </div>
      
      <div>
        <label className="block text-sm font-medium text-[hsl(var(--text-secondary))] mb-2">
          Descripción
        </label>
        <Textarea
          value={content.final_cta.description}
          onChange={(e) => updateField('final_cta', 'description', e.target.value)}
          rows={2}
        />
      </div>
      
      <div>
        <label className="block text-sm font-medium text-[hsl(var(--text-secondary))] mb-2">
          Texto destacado
        </label>
        <Textarea
          value={content.final_cta.highlight}
          onChange={(e) => updateField('final_cta', 'highlight', e.target.value)}
          rows={3}
        />
      </div>
      
      <div>
        <label className="block text-sm font-medium text-[hsl(var(--text-secondary))] mb-2">
          Texto del botón CTA
        </label>
        <Input
          value={content.final_cta.cta_text}
          onChange={(e) => updateField('final_cta', 'cta_text', e.target.value)}
        />
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-[hsl(var(--text-secondary))] mb-2">
            Texto WhatsApp
          </label>
          <Input
            value={content.final_cta.whatsapp_text}
            onChange={(e) => updateField('final_cta', 'whatsapp_text', e.target.value)}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-[hsl(var(--text-secondary))] mb-2">
            Número WhatsApp
          </label>
          <Input
            value={content.final_cta.whatsapp_number}
            onChange={(e) => updateField('final_cta', 'whatsapp_number', e.target.value)}
            placeholder="34612345678"
          />
        </div>
      </div>
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'hero': return renderHeroEditor();
      case 'introduction': return renderIntroductionEditor();
      case 'self_support': return renderSelfSupportEditor();
      case 'structure': return renderStructureEditor();
      case 'learning': return renderLearningEditor();
      case 'dates': return renderDatesEditor();
      case 'pricing': return renderPricingEditor();
      case 'testimonials': return renderTestimonialsEditor();
      case 'instructor': return renderInstructorEditor();
      case 'faqs': return renderFaqsEditor();
      case 'final_cta': return renderFinalCtaEditor();
      default: return null;
    }
  };

  return (
    <div className="pt-20 min-h-screen bg-[hsl(var(--sage-light))]">
      <div className="max-w-6xl mx-auto px-6 lg:px-12 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <Link 
              to="/admin" 
              className="inline-flex items-center gap-2 text-[hsl(var(--text-secondary))] hover:text-[hsl(var(--text-primary))] mb-4 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
              Volver al panel
            </Link>
            <h1 className="text-2xl font-heading text-[hsl(var(--text-primary))]">
              Editor de Landing: Facilitadoras en Sostén Energético
            </h1>
            <p className="text-sm text-[hsl(var(--text-secondary))] mt-1">
              Edita todo el contenido de la página de ventas
            </p>
          </div>
          
          <div className="flex gap-3">
            <a 
              href="/formaciones/facilitadoras-sosten-energetico" 
              target="_blank" 
              rel="noopener noreferrer"
            >
              <Button variant="outline">
                <Eye className="w-4 h-4 mr-2" />
                Ver landing
              </Button>
            </a>
            <Button 
              onClick={handleSave} 
              className="btn-gold"
              disabled={saving}
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Guardar cambios
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-6 p-2 bg-white rounded-xl shadow-soft">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'bg-[hsl(var(--accent))] text-white'
                    : 'text-[hsl(var(--text-secondary))] hover:bg-[hsl(var(--sage-light))]'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Content */}
        <div className="card-soft">
          {renderTabContent()}
        </div>

        {/* Fixed save button for mobile */}
        <div className="fixed bottom-6 right-6 lg:hidden">
          <Button 
            onClick={handleSave} 
            className="btn-gold shadow-lg"
            disabled={saving}
            size="lg"
          >
            {saving ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Save className="w-5 h-5" />
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AdminLandingEditor;
